import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search, Loader2, Globe, ArrowRight, ShieldCheck, Activity, AlertTriangle,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Navbar } from "@/components/Navbar";
import { SitesManager } from "@/components/SitesManager";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { scoreColor } from "@/lib/severity";

export default function Dashboard() {
  const { user } = useAuth();
  const [scans, setScans] = useState([]);
  const [domain, setDomain] = useState("");
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/scans").then((r) => setScans(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runScan = async (e) => {
    e.preventDefault();
    if (!domain.trim()) return;
    setScanning(true);
    try {
      const { data } = await api.post("/scans", { domain });
      toast.success(`Scan complete — ${data.host} scored ${data.score}/100`);
      setDomain("");
      load();
      navigate(`/report/${data.scan_id}`, { state: { justScanned: true } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Scan failed.");
    } finally {
      setScanning(false);
    }
  };

  const latestByHost = Object.values(
    scans.reduce((acc, s) => {
      if (!acc[s.host]) acc[s.host] = s;
      return acc;
    }, {})
  );
  const avg = scans.length
    ? Math.round(latestByHost.reduce((a, s) => a + s.score, 0) / latestByHost.length)
    : 0;
  const openIssues = latestByHost.reduce(
    (a, s) => a + (s.severity_counts.critical || 0) + (s.severity_counts.high || 0),
    0
  );
  const chartData = [...scans]
    .reverse()
    .map((s) => ({ name: new Date(s.scanned_at).toLocaleDateString(), score: s.score }));

  return (
    <div className="min-h-screen text-white">
      <Navbar />
      <main className="pt-24 pb-20 max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyanbrand">Dashboard</p>
            <h1 className="font-heading text-3xl font-semibold mt-2">Hello, {user?.name}</h1>
          </div>
          <span className="text-sm px-3 py-1.5 rounded-full border border-cyanbrand/30 text-cyanbrand bg-cyanbrand/5 capitalize">
            {user?.plan} plan
          </span>
        </div>

        {/* New scan */}
        <form onSubmit={runScan} className="mt-8">
          <div className="flex items-center bg-[#0C1424] border border-slate-700 rounded-md p-2 focus-within:border-cyanbrand transition-all max-w-2xl">
            <Globe size={20} className="text-slate-500 ml-3 shrink-0" />
            <input
              data-testid="dashboard-scan-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="Scan a website — yourwebsite.com"
              className="flex-1 bg-transparent outline-none px-3 py-2 text-white placeholder:text-slate-500 font-mono"
            />
            <button
              data-testid="dashboard-scan-button"
              disabled={scanning}
              className="flex items-center gap-2 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold px-5 py-2.5 rounded-md transition-all disabled:opacity-60"
            >
              {scanning ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              {scanning ? "Scanning…" : "Scan"}
            </button>
          </div>
        </form>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
          <StatCard icon={ShieldCheck} label="Average score" value={`${avg}/100`} color={scoreColor(avg)} />
          <StatCard icon={Globe} label="Sites monitored" value={latestByHost.length} color="#00E5FF" />
          <StatCard icon={AlertTriangle} label="Critical + High open" value={openIssues} color={openIssues ? "#F97316" : "#10B981"} />
        </div>

        {/* Sites / domain verification */}
        <div className="mt-8">
          <SitesManager />
        </div>

        {/* Chart */}
        {chartData.length > 1 && (
          <div className="mt-8 bg-[#0C1424] border border-slate-800 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity size={18} className="text-cyanbrand" />
              <h3 className="font-heading font-semibold">Score over time</h3>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="sc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                <YAxis domain={[0, 100]} stroke="#64748b" fontSize={12} />
                <Tooltip contentStyle={{ background: "#0C1424", border: "1px solid #1e293b", borderRadius: 8, color: "#fff" }} />
                <Area type="monotone" dataKey="score" stroke="#00E5FF" strokeWidth={2} fill="url(#sc)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Sites list */}
        <h2 className="font-heading text-xl font-semibold mt-10 mb-4">Your scans</h2>
        {loading ? (
          <div className="text-slate-400 py-10 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : scans.length === 0 ? (
          <div className="bg-[#0C1424] border border-slate-800 border-dashed rounded-lg p-12 text-center text-slate-400">
            No scans yet. Run your first scan above to get a Security Score.
          </div>
        ) : (
          <div className="space-y-3" data-testid="scan-history">
            {scans.map((s, i) => (
              <motion.button
                key={s.scan_id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.3) }}
                onClick={() => navigate(`/report/${s.scan_id}`)}
                data-testid={`scan-row-${i}`}
                className="w-full flex items-center gap-4 bg-[#0C1424] border border-slate-800 rounded-lg p-4 hover:border-slate-600 transition-all text-left"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center font-heading font-bold text-lg shrink-0"
                  style={{ color: scoreColor(s.score), border: `3px solid ${scoreColor(s.score)}` }}
                >
                  {s.score}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-mono font-semibold truncate">{s.host}</p>
                  <p className="text-sm text-slate-500">
                    {new Date(s.scanned_at).toLocaleString()} · Grade {s.grade}
                  </p>
                </div>
                <div className="hidden sm:flex gap-2 text-xs">
                  {["critical", "high", "medium"].map((sev) =>
                    s.severity_counts[sev] ? (
                      <span key={sev} className="px-2 py-1 rounded-full border border-slate-700 text-slate-300">
                        {s.severity_counts[sev]} {sev}
                      </span>
                    ) : null
                  )}
                </div>
                <ArrowRight size={18} className="text-slate-500 shrink-0" />
              </motion.button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#0C1424] border border-slate-800 rounded-lg p-6">
      <Icon size={22} style={{ color }} strokeWidth={1.75} />
      <p className="text-slate-400 text-sm mt-3">{label}</p>
      <p className="font-heading text-3xl font-bold mt-1" style={{ color }}>{value}</p>
    </div>
  );
}
